import { gql, useMutation } from "@apollo/client";

// Types
export enum OrganizationType {
  NON_PROFIT = "NON_PROFIT",
  FOR_PROFIT = "FOR_PROFIT"
}

export interface LocationInput {
  type: string;
  coordinates: [number, number];
}

export interface ExternalLinkInput {
  name: string;
  url: string;
}

export interface MemberInput {
  name: string;
  role: string;
  email?: string;
  phoneNumber?: string;
  profilePhoto?: string;
}

export interface OrganizationInput {
  name: string;
  profilePhoto?: string;
  description?: string;
  address: string;
  location: LocationInput;
  contact?: string;
  externalLinks?: ExternalLinkInput[];
  organizationType: OrganizationType;
  registrationNumber?: string;
  taxID?: string;
  website?: string;
  socialMedia?: ExternalLinkInput[];
  operatingHours?: any[]; // TimeSlotInput type can be added later if needed
  services?: string[];
  members?: MemberInput[];
}

export interface Organization {
  _id: string;
  name: string;
  profilePhoto?: string;
  description?: string;
  address: string;
  location: {
    type: string;
    coordinates: [number, number];
  };
  contact?: string;
  externalLinks: ExternalLinkInput[];
  organizationType: OrganizationType;
  registrationNumber?: string;
  taxID?: string;
  website?: string;
  socialMedia?: ExternalLinkInput[];
  operatingHours?: any[];
  services?: string[];
  members?: MemberInput[];
  isFreezed: boolean;
  widgets: string[];
  isAssigned: boolean;
  assignedUser?: any[];
  isVerified: boolean;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

// GraphQL Mutation
const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($input: OrganizationInput!) {
    createOrganization(input: $input) {
      _id
      name
      address
      organizationType
      description
      contact
      isVerified
      status
      organizationLinks
    }
  }
`;

// Hook
export const useCreateOrganization = () => {
  const [createOrganization, { loading, error, data }] = useMutation<
    { createOrganization: Organization },
    { input: OrganizationInput }
  >(CREATE_ORGANIZATION);

  return {
    createOrganization: async (input: OrganizationInput) => {
      try {
        const response = await createOrganization({
          variables: { input },
        });
        return response.data?.createOrganization;
      } catch (err) {
        console.error("Error creating organization:", err);
        throw err;
      }
    },
    loading,
    error,
    data: data?.createOrganization,
  };
}; 